<template>
    <div class="roteirizacao-motoristas-panel">
        <div class="d-flex align-center flex-wrap mb-2">
            <span class="text-subtitle-1 font-weight-bold mr-2">{{ $t('routePlanning.drivers.title') }}</span>
            <v-spacer />
            <v-chip v-if="schedule && schedule.motor && schedule.motor.fallback" small color="warning" class="mr-2">
                {{ $t('routePlanning.drivers.fallbackChip') }}
            </v-chip>
        </div>
        <v-alert v-if="error" type="warning" dense text class="mb-3">{{ error }}</v-alert>
        <p v-else-if="!schedule && !error" class="text-body-2 mb-0">
            {{ $t('routePlanning.drivers.empty') }}
        </p>

        <template v-if="schedule">
            <div class="roteirizacao-motoristas-panel__kpis mb-3">
                <v-sheet v-for="k in kpis" :key="k.key" outlined rounded class="roteirizacao-motoristas-panel__kpi">
                    <div class="text-caption text--secondary">{{ k.label }}</div>
                    <div class="text-h6 roteirizacao-motoristas-panel__kpi-value">{{ k.value }}</div>
                    <div v-if="k.sub" class="text-caption text--secondary">{{ k.sub }}</div>
                </v-sheet>
            </div>

            <template v-if="costByType.length">
                <div class="text-caption font-weight-medium mb-1">{{ $t('routePlanning.drivers.costByTypeTitle') }}</div>
                <v-simple-table dense class="roteirizacao-motoristas-panel__by-type mb-3">
                    <thead>
                        <tr>
                            <th>{{ $t('routePlanning.drivers.mainType') }}</th>
                            <th class="text-right">{{ $t('routePlanning.drivers.driversColumn') }}</th>
                            <th class="text-right">{{ $t('routePlanning.drivers.xlsx.cost') }}</th>
                            <th class="text-right">{{ $t('routePlanning.drivers.costPerDriver') }}</th>
                            <th class="roteirizacao-motoristas-panel__share-col" />
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in costByType" :key="row.key">
                            <td>{{ row.label }}</td>
                            <td class="text-right">{{ row.drivers }}</td>
                            <td class="text-right">{{ formatMoney(row.cost) }}</td>
                            <td class="text-right">{{ formatMoney(row.perDriver) }}</td>
                            <td>
                                <div class="roteirizacao-motoristas-panel__share">
                                    <div
                                        class="roteirizacao-motoristas-panel__share-bar"
                                        :style="{ width: `${row.share}%` }"
                                    />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </v-simple-table>
            </template>
            <v-tabs v-model="tab" height="36" show-arrows class="mb-2">
                <v-tab href="#jornada">{{ $t('routePlanning.drivers.tabs.jornada') }}</v-tab>
                <v-tab href="#drivers">
                    {{ $t('routePlanning.drivers.tabs.drivers') }}
                    <HelpGuiasDialog
                        :help-text="$t('routePlanning.drivers.help.drivers.text')"
                        :help-items="driversHelpItems"
                        :max-width="420"
                    />
                </v-tab>
                <v-tab href="#overlap">{{ $t('routePlanning.drivers.tabs.overlap') }}</v-tab>
                <v-tab v-if="routeImpacts.length" href="#impacts">
                    {{ $t('routePlanning.drivers.tabs.impacts') }}
                    <v-chip x-small class="ml-1">{{ routeImpacts.length }}</v-chip>
                    <HelpGuiasDialog
                        :help-text="$t('routePlanning.drivers.help.impacts.text')"
                        :help-items="impactsHelpItems"
                        :max-width="420"
                    />
                </v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab" class="mb-3">
                <v-tab-item value="jornada">
                    <RoteirizacaoMotoristasjornada :drivers="drivers" />
                </v-tab-item>

                <v-tab-item value="drivers">
                    <v-data-table
                        dense
                        :headers="headers"
                        :items="driverRows"
                        item-key="id"
                        show-expand
                        single-expand
                        :mobile-breakpoint="0"
                        :items-per-page="10"
                    >
                        <template v-slot:item.total="{ item }">
                            <span :class="{ 'error--text': item.overLimit }">{{ item.totalText }}</span>
                        </template>
                        <template v-slot:item.lunch="{ item }">
                            <v-chip v-if="item.lunchMissing" x-small color="warning">
                                {{ $t('routePlanning.drivers.lunchMissingShort') }}
                            </v-chip>
                            <span v-else>{{ item.lunch }}</span>
                        </template>
                        <template v-slot:item.reason="{ item }">
                            {{ reasonLabel(item.reason) }}
                        </template>
                        <template v-slot:item.cost="{ item }">
                            {{ formatMoney(item.cost) }}
                        </template>
                        <template v-slot:expanded-item="{ headers: hs, item }">
                            <td :colspan="hs.length" class="py-2">
                                <div class="roteirizacao-motoristas-panel__timeline mb-2">
                                    <div
                                        v-for="seg in item.timeline"
                                        :key="seg.key"
                                        class="roteirizacao-motoristas-panel__segment"
                                        :style="{ left: seg.left, width: seg.width, background: seg.color }"
                                        :title="seg.title"
                                    />
                                </div>
                                <v-simple-table dense>
                                    <thead>
                                        <tr>
                                            <th>{{ $t('routePlanning.drivers.xlsx.day') }}</th>
                                            <th>{{ $t('routePlanning.drivers.xlsx.route') }}</th>
                                            <th>{{ $t('routePlanning.drivers.xlsx.direction') }}</th>
                                            <th>{{ $t('routePlanning.drivers.xlsx.cycle') }}</th>
                                            <th>{{ $t('routePlanning.drivers.xlsx.vehicleType') }}</th>
                                            <th>{{ $t('routePlanning.drivers.xlsx.vehicle') }}</th>
                                            <th>{{ $t('routePlanning.drivers.xlsx.start') }}</th>
                                            <th>{{ $t('routePlanning.drivers.xlsx.end') }}</th>
                                            <th class="text-right">{{ $t('routePlanning.drivers.xlsx.displacementMin') }}</th>
                                            <th class="text-right">{{ $t('routePlanning.drivers.xlsx.transferMin') }}</th>
                                            <th class="text-right">{{ $t('routePlanning.drivers.xlsx.waitMin') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="b in item.blocks" :key="b.blocoId">
                                            <td>{{ b.dia }}</td>
                                            <td>{{ (b.routeNumbers || []).join(', ') }}</td>
                                            <td>{{ $t(`routePlanning.direction.${b.sentido}`) }}</td>
                                            <td>{{ b.ciclo ? cycleLabel(b.ciclo) : '—' }}</td>
                                            <td>{{ typeLabel(b.typeKey) }}</td>
                                            <td>{{ b.physicalVehicleId != null ? b.physicalVehicleId : '—' }}</td>
                                            <td>{{ b.inicio }}</td>
                                            <td>{{ b.fim }}</td>
                                            <td class="text-right">{{ b.deslocamentoMin != null ? b.deslocamentoMin : '—' }}</td>
                                            <td class="text-right">{{ transferText(b) }}</td>
                                            <td class="text-right">{{ b.esperaMin != null ? b.esperaMin : '—' }}</td>
                                        </tr>
                                    </tbody>
                                </v-simple-table>
                            </td>
                        </template>
                    </v-data-table>
                </v-tab-item>

                <v-tab-item value="overlap">
                    <div class="text-caption font-weight-medium mb-1">{{ $t('routePlanning.drivers.overlapTitle') }}</div>
                    <div class="roteirizacao-motoristas-panel__overlap mb-1">
                        <div
                            v-for="h in overlapHours"
                            :key="h.hora"
                            class="roteirizacao-motoristas-panel__overlap-col"
                            :title="overlapTooltip(h)"
                        >
                            <div class="roteirizacao-motoristas-panel__overlap-bars">
                                <div
                                    class="roteirizacao-motoristas-panel__bar"
                                    :style="{ height: barHeight(h.picoMotoristas), background: colors.drivers }"
                                />
                                <div
                                    class="roteirizacao-motoristas-panel__bar"
                                    :style="{ height: barHeight(h.picoVeiculos), background: colors.vehicles }"
                                />
                            </div>
                            <div
                                class="roteirizacao-motoristas-panel__impact"
                                :style="{ background: h.impacto ? colors.impact[h.impacto] : 'transparent' }"
                            />
                            <div class="roteirizacao-motoristas-panel__hour">{{ h.hora.slice(0, 2) }}</div>
                        </div>
                    </div>
                    <div class="d-flex flex-wrap text-caption">
                        <span v-for="item in overlapLegend" :key="item.label" class="mr-3">
                            <span class="roteirizacao-motoristas-panel__legend" :style="{ background: item.color }" />
                            {{ item.label }}
                        </span>
                    </div>
                </v-tab-item>

                <v-tab-item v-if="routeImpacts.length" value="impacts">
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>{{ $t('routePlanning.drivers.xlsx.route') }}</th>
                                <th>{{ $t('routePlanning.drivers.xlsx.vehicleType') }}</th>
                                <th>{{ $t('routePlanning.drivers.xlsx.cycle') }}</th>
                                <th>{{ $t('routePlanning.drivers.xlsx.start') }}</th>
                                <th>{{ $t('routePlanning.drivers.impactColumn') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="r in routeImpacts" :key="r.bloco">
                                <td>{{ r.routeNumber }}</td>
                                <td>{{ typeLabel(r.typeKey) }}</td>
                                <td>{{ r.ciclo ? cycleLabel(r.ciclo) : '—' }}</td>
                                <td>{{ r.inicio }}</td>
                                <td>{{ $t(`routePlanning.drivers.impact.${r.impacto}`) }}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-tab-item>
            </v-tabs-items>

            <v-alert v-if="(schedule.descartes || []).length" type="info" dense text class="mb-0">
                {{ $t('routePlanning.drivers.discarded', { n: schedule.descartes.length, routes: discardedRoutes }) }}
            </v-alert>
        </template>
    </div>
</template>

<script>
import HelpGuiasDialog from '../HelpGuiasDialog.vue';
import RoteirizacaoMotoristasjornada from './RoteirizacaoMotoristasjornada.vue';
import {
    DRIVER_SEGMENT_COLORS,
    DRIVER_SEGMENT_TYPES,
    driverTimeline,
    minToClock,
    minToDuration,
    typeKeyLabel,
    wrapIntoDay,
} from '@/utils/roteirizacaoMotoristas';

/** Cores do gráfico de overlap (tela e PDF usam as mesmas). */
const COLORS = {
    drivers: '#1976d2',
    vehicles: '#90caf9',
    impact: {
        motorista_extra: '#fb8c00',
        veiculo_nao_reaproveitado: '#8e24aa',
        ambos: '#e53935',
    },
};

function stats(list) {
    const xs = list.filter((v) => Number.isFinite(v));
    if (!xs.length) return { avg: 0, max: 0, min: 0 };
    return {
        avg: xs.reduce((a, b) => a + b, 0) / xs.length,
        max: Math.max(...xs),
        min: Math.min(...xs),
    };
}

/** "8:00" ou "7:00–8:00" quando os limites variam por modal. */
function limitText(list) {
    const s = stats(list);
    if (!s.max) return '';
    return s.min === s.max ? minToDuration(s.max) : `${minToDuration(s.min)}–${minToDuration(s.max)}`;
}

export default {
    name: 'RoteirizacaoMotoristasPanel',
    components: { RoteirizacaoMotoristasjornada, HelpGuiasDialog },
    props: {
        /** Resultado do worker ROTEIRIZACAO_MOTORISTAS (body). */
        schedule: { type: Object, default: null },
        error: { type: String, default: '' },
        fileName: { type: String, default: 'motoristas.xlsx' },
    },
    data() {
        return {
            exporting: false,
            colors: COLORS,
            impactKinds: ['motorista_extra', 'veiculo_nao_reaproveitado', 'ambos'],
            tab: 'jornada',
        };
    },
    computed: {
        headers() {
            return [
                { text: '#', value: 'id', width: 50 },
                { text: this.$t('routePlanning.drivers.mainType'), value: 'typeLabel' },
                { text: this.$t('routePlanning.drivers.cyclesColumn'), value: 'cycles' },
                { text: this.$t('routePlanning.drivers.xlsx.shiftStart'), value: 'start' },
                { text: this.$t('routePlanning.drivers.xlsx.shiftEnd'), value: 'end' },
                { text: this.$t('routePlanning.drivers.totalShift'), value: 'total', align: 'end' },
                { text: this.$t('routePlanning.drivers.netShift'), value: 'netText', align: 'end' },
                { text: this.$t('routePlanning.drivers.jornada.types.espera'), value: 'idleText', align: 'end' },
                { text: this.$t('routePlanning.drivers.xlsx.lunch'), value: 'lunch' },
                { text: this.$t('routePlanning.drivers.blocksColumn'), value: 'blockCount', align: 'end' },
                { text: this.$t('routePlanning.drivers.xlsx.cost'), value: 'cost', align: 'end' },
                { text: this.$t('routePlanning.drivers.xlsx.creationReason'), value: 'reason' },
                { text: '', value: 'data-table-expand' },
            ];
        },
        /** Por que cada motorista foi criado (ícone de ajuda na aba Motoristas). */
        driversHelpItems() {
            return ['first', 'jornada', 'almoco', 'veiculo', 'modal', 'sem_folga', 'sobreposicao', 'sem_candidato', 'priority']
                .map((k) => this.$t(`routePlanning.drivers.help.drivers.${k}`));
        },
        /** Por que cada rota tem impacto (ícone de ajuda na aba Impactos). */
        impactsHelpItems() {
            return [...this.impactKinds, 'none']
                .map((k) => this.$t(`routePlanning.drivers.help.impacts.${k}`));
        },
        drivers() {
            return (this.schedule && this.schedule.motoristas) || [];
        },
        timelines() {
            return this.drivers.map((m) => driverTimeline(m));
        },
        driverRows() {
            return this.drivers.map((m, idx) => {
                const j = m.jornada || {};
                const total = j.totalMin != null ? j.totalMin : null;
                return {
                    id: m.id,
                    typeLabel: typeKeyLabel(m.modalPrincipal),
                    cycles: (m.ciclos || []).map((c) => this.cycleLabel(c)).join(', '),
                    start: j.inicio,
                    end: j.fim,
                    total,
                    totalText: total != null ? minToDuration(total) : '—',
                    overLimit: total != null && j.limiteTotalMin != null && total > j.limiteTotalMin + 1e-9,
                    net: Math.round(j.liquidaMin || 0),
                    netText: minToDuration(j.liquidaMin),
                    idleText: j.esperaMin != null ? minToDuration(j.esperaMin) : '—',
                    lunch: j.almoco ? `${j.almoco.inicio}–${j.almoco.fim}` : '—',
                    lunchMissing: !!(j.almocoExigido && !j.almoco),
                    blockCount: (m.blocos || []).length,
                    cost: m.custo,
                    reason: m.motivoCriacao,
                    blocks: m.blocos || [],
                    timeline: this.rowTimeline(this.timelines[idx]),
                };
            });
        },
        totalStats() {
            return stats(this.drivers.map((m) => Number(m.jornada && m.jornada.totalMin)));
        },
        netStats() {
            return stats(this.drivers.map((m) => Number(m.jornada && m.jornada.liquidaMin)));
        },
        /** Indicadores do topo: dimensionamento, custo, jornada total/líquida e utilização. */
        kpis() {
            const t = (k, p) => this.$t(k, p);
            const n = this.drivers.length;
            const sumTotal = this.drivers.reduce((a, m) => a + (Number(m.jornada.totalMin) || 0), 0);
            const sumNet = this.drivers.reduce((a, m) => a + (Number(m.jornada.liquidaMin) || 0), 0);
            const sumIdle = this.drivers.reduce((a, m) => a + (Number(m.jornada.esperaMin) || 0), 0);
            const withLunch = this.drivers.filter((m) => m.jornada.almoco).length;
            const totalLimit = limitText(this.drivers.map((m) => Number(m.jornada.limiteTotalMin)));
            const netLimit = limitText(this.drivers.map((m) => Number(m.jornada.limiteMin)));
            const hasTotal = this.drivers.some((m) => m.jornada.totalMin != null);
            const list = [
                {
                    key: 'drivers',
                    label: t('routePlanning.drivers.uniqueTotal'),
                    value: String(this.schedule.totalUnico),
                    sub: t('routePlanning.drivers.kpi.withLunch', { n: withLunch }),
                },
                {
                    key: 'cost',
                    label: t('routePlanning.drivers.costTotal'),
                    value: this.formatMoney(this.schedule.custoTotal),
                    sub: n ? `${t('routePlanning.drivers.costPerDriver')}: ${this.formatMoney(this.schedule.custoTotal / n)}` : '',
                },
            ];
            if (hasTotal) {
                list.push({
                    key: 'total',
                    label: t('routePlanning.drivers.kpi.totalAvgMax'),
                    value: `${minToDuration(this.totalStats.avg)} / ${minToDuration(this.totalStats.max)}`,
                    sub: totalLimit ? t('routePlanning.drivers.kpi.limit', { value: totalLimit }) : '',
                });
            }
            list.push({
                key: 'net',
                label: t('routePlanning.drivers.netShiftAvgMax'),
                value: `${minToDuration(this.netStats.avg)} / ${minToDuration(this.netStats.max)}`,
                sub: netLimit ? t('routePlanning.drivers.kpi.limit', { value: netLimit }) : '',
            });
            if (hasTotal && sumTotal > 0) {
                list.push({
                    key: 'use',
                    label: t('routePlanning.drivers.kpi.utilization'),
                    value: `${Math.round((sumNet / sumTotal) * 100)}%`,
                    sub: t('routePlanning.drivers.kpi.idleAvg', { value: minToDuration(n ? sumIdle / n : 0) }),
                });
            }
            return list;
        },
        costByType() {
            const por = (this.schedule && this.schedule.porModal) || {};
            const total = Object.values(por).reduce((a, r) => a + (Number(r && r.custo) || 0), 0);
            return Object.keys(por).sort().map((key) => {
                const r = por[key] || {};
                const cost = Number(r.custo) || 0;
                const drivers = Number(r.motoristas) || 0;
                return {
                    key,
                    label: typeKeyLabel(key),
                    drivers,
                    cost,
                    perDriver: drivers ? cost / drivers : 0,
                    share: total > 0 ? Math.round((cost / total) * 100) : 0,
                };
            });
        },
        overlapHours() {
            return (this.schedule && this.schedule.overlap && this.schedule.overlap.porHora) || [];
        },
        overlapLegend() {
            return [
                { label: this.$t('routePlanning.drivers.legendDrivers'), color: COLORS.drivers },
                { label: this.$t('routePlanning.drivers.legendVehicles'), color: COLORS.vehicles },
                ...this.impactKinds.map((k) => ({
                    label: this.$t(`routePlanning.drivers.impact.${k}`),
                    color: COLORS.impact[k],
                })),
            ];
        },
        segmentLegend() {
            return DRIVER_SEGMENT_TYPES.map((tipo) => ({
                label: this.$t(`routePlanning.drivers.jornada.types.${tipo}`),
                color: DRIVER_SEGMENT_COLORS[tipo],
            }));
        },
        maxHourly() {
            return Math.max(1, ...this.overlapHours.map((h) => Math.max(h.picoMotoristas, h.picoVeiculos)));
        },
        routeImpacts() {
            return (this.schedule && this.schedule.overlap && this.schedule.overlap.porRota) || [];
        },
        discardedRoutes() {
            return ((this.schedule && this.schedule.descartes) || [])
                .map((d) => d.routeNumber).filter((n) => n != null).join(', ') || '—';
        },
        /**
         * Memória de cálculo dos motoristas no PDF: parâmetros e regras usados pelo worker, a
         * conta da jornada de cada motorista (pré + deslocamento + rota + transferência + espera
         * + pós; almoço fora) contra os limites e o rateio do custo por rota.
         */
        pdfCalcSections() {
            if (!this.schedule) return [];
            const t = (k, p) => this.$t(k, p);
            const p = this.schedule.params || {};
            const minText = (v) => (v != null && v !== '' ? `${Math.round(Number(v))} min` : '—');
            const two = [
                { text: t('routePlanning.sidebar.costPdfField'), value: 'label' },
                { text: t('routePlanning.sidebar.costPdfValue'), value: 'value' },
            ];
            const limits = {};
            this.drivers.forEach((m) => {
                const j = m.jornada || {};
                if (j.limiteMin != null) limits[m.modalPrincipal] = { net: j.limiteMin, total: j.limiteTotalMin };
            });
            const params = [
                { label: t('routePlanning.drivers.params.preShift'), value: minText(p.preJornadaMin) },
                { label: t('routePlanning.drivers.params.postShift'), value: minText(p.posJornadaMin) },
                {
                    label: t('routePlanning.drivers.params.lunch'),
                    value: p.aplicarAlmoco === false || !Number(p.almocoMin)
                        ? t('routePlanning.drivers.pdf.lunchOff')
                        : t('routePlanning.drivers.pdf.lunchOn', { min: Math.round(Number(p.almocoMin)) }),
                },
                { label: t('routePlanning.drivers.pdf.minTransfer'), value: minText(p.tempoMinimoTransferenciaMin) },
                ...Object.keys(limits).sort().map((tk) => ({
                    label: t('routePlanning.drivers.pdf.netShiftOfType', { type: typeKeyLabel(tk) }),
                    value: t('routePlanning.drivers.pdf.netShiftValue', {
                        net: minToDuration(limits[tk].net),
                        total: limits[tk].total != null ? minToDuration(limits[tk].total) : '—',
                    }),
                })),
                ...(p.ciclos || []).map((c) => ({
                    label: t('routePlanning.drivers.pdf.cycle', { name: this.cycleLabel(c.ciclo) }),
                    value: t('routePlanning.drivers.pdf.cycleValue', {
                        start: c.inicio,
                        end: c.fim,
                        swap: t(`routePlanning.drivers.${c.trocaVeiculo === false ? 'no' : 'yes'}`),
                    }),
                })),
                { label: t('routePlanning.drivers.totalShift'), value: t('routePlanning.drivers.pdf.formulaTotal') },
                { label: t('routePlanning.drivers.netShift'), value: t('routePlanning.drivers.pdf.formulaNet') },
                { label: t('routePlanning.drivers.pdf.lunchRuleLabel'), value: t('routePlanning.drivers.pdf.formulaLunch') },
                { label: t('routePlanning.drivers.pdf.vehicleRuleLabel'), value: t('routePlanning.drivers.pdf.formulaVehicle') },
                { label: t('routePlanning.drivers.xlsx.cost'), value: t('routePlanning.drivers.pdf.formulaCost') },
            ];
            const pre = Number(p.preJornadaMin) || 0;
            const pos = Number(p.posJornadaMin) || 0;
            const lunchMin = Number(p.almocoMin) || 0;
            const status = (j) => {
                const key = `routePlanning.drivers.pdf.status.${j.motivo || 'ok'}`;
                return this.$te(key) ? t(key) : String(j.motivo);
            };
            const sections = [
                { sectionTitle: t('routePlanning.drivers.pdf.paramsTitle'), headers: two, content: params },
                {
                    sectionTitle: t('routePlanning.drivers.pdf.calcTitle'),
                    headers: [
                        { text: '#', value: 'id' },
                        { text: t('routePlanning.drivers.mainType'), value: 'type' },
                        { text: t('routePlanning.drivers.jornada.types.preJornada'), value: 'pre' },
                        { text: t('routePlanning.drivers.jornada.types.deslocamento'), value: 'displacement' },
                        { text: t('routePlanning.drivers.jornada.types.principal'), value: 'main' },
                        { text: t('routePlanning.drivers.jornada.types.transferencia'), value: 'transfer' },
                        { text: t('routePlanning.drivers.jornada.types.espera'), value: 'idle' },
                        { text: t('routePlanning.drivers.jornada.types.posJornada'), value: 'pos' },
                        { text: t('routePlanning.drivers.pdf.totalVsLimit'), value: 'total' },
                        { text: t('routePlanning.drivers.pdf.netVsLimit'), value: 'net' },
                        { text: t('routePlanning.drivers.jornada.types.almoco'), value: 'lunch' },
                        { text: t('routePlanning.drivers.pdf.statusColumn'), value: 'status' },
                        { text: t('routePlanning.drivers.xlsx.cost'), value: 'cost' },
                    ],
                    content: this.drivers.map((m) => {
                        const j = m.jornada || {};
                        let lunch = '—';
                        if (j.almoco) lunch = `${j.almoco.inicio}–${j.almoco.fim} (${lunchMin} min)`;
                        else if (j.almocoExigido) lunch = t('routePlanning.drivers.lunchMissingShort');
                        return {
                            id: String(m.id),
                            type: typeKeyLabel(m.modalPrincipal),
                            pre: minToDuration(pre),
                            displacement: minToDuration(j.deslocamentoMin),
                            main: minToDuration(j.principalMin),
                            transfer: minToDuration(j.transferenciaMin),
                            idle: minToDuration(j.esperaMin),
                            pos: minToDuration(pos),
                            total: `${minToDuration(j.totalMin)} / ${minToDuration(j.limiteTotalMin)}`,
                            net: `${minToDuration(j.liquidaMin)} / ${minToDuration(j.limiteMin)}`,
                            lunch,
                            status: status(j),
                            cost: this.formatMoney(m.custo),
                        };
                    }),
                },
            ];
            const perRoute = this.schedule.custoPorRota || {};
            const routeKeys = Object.keys(perRoute)
                .sort((a, b) => (Number(a) - Number(b)) || a.localeCompare(b));
            if (routeKeys.length) {
                sections.push({
                    sectionTitle: t('routePlanning.drivers.pdf.costPerRouteTitle'),
                    headers: [
                        { text: t('routePlanning.drivers.xlsx.route'), value: 'route' },
                        { text: t('routePlanning.drivers.pdf.driverCostColumn'), value: 'cost' },
                    ],
                    content: routeKeys.map((k) => ({ route: k, cost: this.formatMoney(perRoute[k]) })),
                });
            }
            return sections;
        },
        /**
         * Seções do PDF do card de custos (ExportTablePdf): indicadores, custo por modal, memória
         * de cálculo, gráfico de overlap em 24h, jornada de todos os motoristas, motoristas, blocos
         * e impactos por rota.
         */
        pdfSections() {
            if (!this.schedule) return [];
            const t = (k, p) => this.$t(k, p);
            const two = [
                { text: t('routePlanning.sidebar.costPdfField'), value: 'label' },
                { text: t('routePlanning.sidebar.costPdfValue'), value: 'value' },
            ];
            const sections = [{
                sectionTitle: t('routePlanning.drivers.title'),
                headers: two,
                content: [
                    ...this.kpis.map((k) => ({ label: k.label, value: k.sub ? `${k.value} (${k.sub})` : k.value })),
                    ...(this.schedule.motor && this.schedule.motor.fallback
                        ? [{ label: t('routePlanning.drivers.fallbackChip'), value: '!' }]
                        : []),
                ],
            }];
            if (this.costByType.length) {
                sections.push({
                    sectionTitle: t('routePlanning.drivers.costByTypeTitle'),
                    headers: [
                        { text: t('routePlanning.drivers.mainType'), value: 'label' },
                        { text: t('routePlanning.drivers.driversColumn'), value: 'drivers' },
                        { text: t('routePlanning.drivers.xlsx.cost'), value: 'cost' },
                        { text: t('routePlanning.drivers.costPerDriver'), value: 'perDriver' },
                    ],
                    content: this.costByType.map((r) => ({
                        label: r.label,
                        drivers: String(r.drivers),
                        cost: this.formatMoney(r.cost),
                        perDriver: this.formatMoney(r.perDriver),
                    })),
                });
                sections.push({
                    sectionTitle: t('routePlanning.drivers.pdf.costByTypeChartTitle'),
                    chart: this.buildCostBarsChart(
                        this.costByType.map((r) => r.label),
                        this.costByType.map((r) => r.cost),
                        t('routePlanning.drivers.xlsx.cost'),
                    ),
                });
            }
            if (this.drivers.length) {
                sections.push({
                    sectionTitle: t('routePlanning.drivers.pdf.costPerDriverChartTitle'),
                    chart: this.buildCostBarsChart(
                        this.drivers.map((m) => `#${m.id}`),
                        this.drivers.map((m) => Number(m.custo) || 0),
                        t('routePlanning.drivers.xlsx.cost'),
                        // Com muitos motoristas os valores não cabem sobre as barras.
                        this.drivers.length <= 12,
                    ),
                });
            }
            sections.push(...this.pdfCalcSections);
            if (this.overlapHours.length) {
                sections.push({
                    sectionTitle: t('routePlanning.drivers.overlapTitle'),
                    chart: {
                        kind: 'bars',
                        labels: this.overlapHours.map((h) => h.hora.slice(0, 2)),
                        series: [
                            { label: t('routePlanning.drivers.legendDrivers'), color: COLORS.drivers,
                                values: this.overlapHours.map((h) => h.picoMotoristas) },
                            { label: t('routePlanning.drivers.legendVehicles'), color: COLORS.vehicles,
                                values: this.overlapHours.map((h) => h.picoVeiculos) },
                        ],
                        markers: this.overlapHours.map((h) => (h.impacto ? COLORS.impact[h.impacto] : null)),
                        legend: this.overlapLegend,
                    },
                });
            }
            const lines = this.timelines.filter(Boolean);
            if (lines.length) {
                // Mesmas 24 colunas da tela (00h–23h); o que passa de 24:00 volta ao início.
                const start = 0;
                const end = 1440;
                const ticks = [];
                for (let m = start; m < end; m += 60) ticks.push({ at: m, label: minToClock(m).slice(0, 2) });
                sections.push({
                    sectionTitle: t('routePlanning.drivers.jornada.title'),
                    chart: {
                        kind: 'timeline',
                        start,
                        end,
                        ticks,
                        rows: this.timelines.map((l, idx) => ({
                            label: `#${this.drivers[idx].id} ${typeKeyLabel(this.drivers[idx].modalPrincipal)}`,
                            segments: l ? l.segments.flatMap((s) => wrapIntoDay(s.ini, s.fim).map((piece) => ({
                                ini: piece.ini,
                                fim: piece.fim,
                                color: DRIVER_SEGMENT_COLORS[s.tipo] || DRIVER_SEGMENT_COLORS.principal,
                            }))) : [],
                        })),
                        legend: this.segmentLegend,
                    },
                });
            }
            sections.push({
                sectionTitle: t('routePlanning.drivers.driversTableTitle'),
                headers: [
                    { text: '#', value: 'id' },
                    { text: t('routePlanning.drivers.mainType'), value: 'typeLabel' },
                    { text: t('routePlanning.drivers.cyclesColumn'), value: 'cycles' },
                    { text: t('routePlanning.drivers.xlsx.shiftStart'), value: 'start' },
                    { text: t('routePlanning.drivers.xlsx.shiftEnd'), value: 'end' },
                    { text: t('routePlanning.drivers.totalShift'), value: 'total' },
                    { text: t('routePlanning.drivers.netShift'), value: 'net' },
                    { text: t('routePlanning.drivers.jornada.types.espera'), value: 'idle' },
                    { text: t('routePlanning.drivers.xlsx.lunch'), value: 'lunch' },
                    { text: t('routePlanning.drivers.blocksColumn'), value: 'blockCount' },
                    { text: t('routePlanning.drivers.xlsx.cost'), value: 'cost' },
                    { text: t('routePlanning.drivers.xlsx.creationReason'), value: 'reason' },
                ],
                content: this.driverRows.map((r) => ({
                    id: String(r.id),
                    typeLabel: r.typeLabel,
                    cycles: r.cycles,
                    start: r.start,
                    end: r.end,
                    total: r.totalText,
                    net: r.netText,
                    idle: r.idleText,
                    lunch: r.lunchMissing ? t('routePlanning.drivers.lunchMissingShort') : r.lunch,
                    blockCount: String(r.blockCount),
                    cost: this.formatMoney(r.cost),
                    reason: this.reasonLabel(r.reason),
                })),
            });
            sections.push({
                sectionTitle: t('routePlanning.drivers.blocksTableTitle'),
                headers: [
                    { text: t('routePlanning.drivers.xlsx.driver'), value: 'driver' },
                    { text: t('routePlanning.drivers.xlsx.day'), value: 'dia' },
                    { text: t('routePlanning.drivers.xlsx.route'), value: 'route' },
                    { text: t('routePlanning.drivers.xlsx.direction'), value: 'direction' },
                    { text: t('routePlanning.drivers.xlsx.cycle'), value: 'cycle' },
                    { text: t('routePlanning.drivers.xlsx.vehicleType'), value: 'type' },
                    { text: t('routePlanning.drivers.xlsx.vehicle'), value: 'vehicle' },
                    { text: t('routePlanning.drivers.xlsx.start'), value: 'start' },
                    { text: t('routePlanning.drivers.xlsx.end'), value: 'end' },
                    { text: t('routePlanning.drivers.xlsx.transferMin'), value: 'transfer' },
                    { text: t('routePlanning.drivers.xlsx.waitMin'), value: 'wait' },
                ],
                content: this.drivers.flatMap((m) => (m.blocos || []).map((b) => ({
                    driver: String(m.id),
                    dia: b.dia,
                    route: (b.routeNumbers || []).join(', '),
                    direction: t(`routePlanning.direction.${b.sentido}`),
                    cycle: b.ciclo ? this.cycleLabel(b.ciclo) : '—',
                    type: typeKeyLabel(b.typeKey),
                    vehicle: b.physicalVehicleId != null ? String(b.physicalVehicleId) : '—',
                    start: b.inicio,
                    end: b.fim,
                    transfer: this.transferText(b),
                    wait: b.esperaMin != null ? String(b.esperaMin) : '—',
                }))),
            });
            if (this.routeImpacts.length) {
                sections.push({
                    sectionTitle: t('routePlanning.drivers.routeImpactsTitle'),
                    headers: [
                        { text: t('routePlanning.drivers.xlsx.route'), value: 'route' },
                        { text: t('routePlanning.drivers.xlsx.vehicleType'), value: 'type' },
                        { text: t('routePlanning.drivers.xlsx.cycle'), value: 'cycle' },
                        { text: t('routePlanning.drivers.xlsx.start'), value: 'start' },
                        { text: t('routePlanning.drivers.impactColumn'), value: 'impact' },
                    ],
                    content: this.routeImpacts.map((r) => ({
                        route: String(r.routeNumber),
                        type: typeKeyLabel(r.typeKey),
                        cycle: r.ciclo ? this.cycleLabel(r.ciclo) : '—',
                        start: r.inicio,
                        impact: t(`routePlanning.drivers.impact.${r.impacto}`),
                    })),
                });
            }
            return sections;
        },
    },
    methods: {
        /** Gráfico de barras de uma série de custos para o PDF (valores monetários formatados). */
        buildCostBarsChart(labels, values, seriesLabel, showValues = true) {
            const max = Math.max(0, ...values);
            return {
                kind: 'bars',
                labels,
                maxLabel: this.formatMoney(max),
                series: [{
                    label: seriesLabel,
                    color: COLORS.drivers,
                    values,
                    valueLabels: showValues ? values.map((v) => this.formatMoney(v)) : undefined,
                }],
                legend: [{ label: seriesLabel, color: COLORS.drivers }],
            };
        },
        /** Mesmo formato do painel de custo (MXN em es-MX, BRL nos demais). */
        formatMoney(value) {
            const n = Number(value);
            if (!Number.isFinite(n)) return '—';
            const isEs = this.$i18n && String(this.$i18n.locale).trim() === 'es-MX';
            return n.toLocaleString(isEs ? 'es-MX' : 'pt-BR', { style: 'currency', currency: isEs ? 'MXN' : 'BRL' });
        },
        typeLabel(key) {
            return typeKeyLabel(key);
        },
        /** Turno padrão (AM1, AM2PM, SD) traduzido; turno da unidade pelo próprio código. */
        cycleLabel(ciclo) {
            const key = `routePlanning.drivers.cycles.${ciclo}`;
            return this.$te(key) ? this.$t(key) : ciclo;
        },
        barHeight(n) {
            return `${Math.round((Number(n) || 0) / this.maxHourly * 100)}%`;
        },
        overlapTooltip(h) {
            const parts = [
                `${h.hora}`,
                `${this.$t('routePlanning.drivers.legendDrivers')}: ${h.picoMotoristas}`,
                `${this.$t('routePlanning.drivers.legendVehicles')}: ${h.picoVeiculos}`,
            ];
            if (h.impacto) parts.push(this.$t(`routePlanning.drivers.impact.${h.impacto}`));
            return parts.join(' · ');
        },
        reasonLabel(reason) {
            const key = String(reason || '').replace(':', '_');
            return this.$te(`routePlanning.drivers.reasons.${key}`)
                ? this.$t(`routePlanning.drivers.reasons.${key}`)
                : reason;
        },
        transferLabel(origem) {
            return this.$te(`routePlanning.drivers.transfer.${origem}`)
                ? this.$t(`routePlanning.drivers.transfer.${origem}`)
                : origem;
        },
        transferText(b) {
            return b.transferenciaMin != null
                ? `${b.transferenciaMin} (${this.transferLabel(b.transferenciaOrigem)})`
                : '—';
        },
        /** Segmentos da jornada (mesmos do jornada) em % da própria jornada. */
        rowTimeline(line) {
            if (!line) return [];
            const total = Math.max(1, line.end - line.start);
            return line.segments.map((s, i) => ({
                key: `s${i}`,
                color: DRIVER_SEGMENT_COLORS[s.tipo] || DRIVER_SEGMENT_COLORS.principal,
                title: `${this.$t(`routePlanning.drivers.jornada.types.${s.tipo}`)} · ${minToClock(s.ini)}–${minToClock(s.fim)}`,
                left: `${((s.ini - line.start) / total) * 100}%`,
                width: `${Math.max(0.5, ((s.fim - s.ini) / total) * 100)}%`,
            }));
        }
    },
};
</script>

<style scoped>
.roteirizacao-motoristas-panel__kpis {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 8px;
}
.roteirizacao-motoristas-panel__kpi {
    padding: 8px 10px;
    min-width: 0;
}
.roteirizacao-motoristas-panel__kpi-value {
    line-height: 1.3 !important;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.roteirizacao-motoristas-panel__share-col {
    width: 30%;
}
.roteirizacao-motoristas-panel__share {
    height: 8px;
    background: #eceff1;
    border-radius: 4px;
    overflow: hidden;
}
.roteirizacao-motoristas-panel__share-bar {
    height: 100%;
    background: #1976d2;
}
.roteirizacao-motoristas-panel__overlap {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 96px;
}
.roteirizacao-motoristas-panel__overlap-col {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0;
}
.roteirizacao-motoristas-panel__overlap-bars {
    flex: 1 1 auto;
    display: flex;
    align-items: flex-end;
    gap: 1px;
}
.roteirizacao-motoristas-panel__bar {
    flex: 1 1 0;
    border-radius: 2px 2px 0 0;
}
.roteirizacao-motoristas-panel__impact {
    height: 4px;
    margin-top: 2px;
}
.roteirizacao-motoristas-panel__hour {
    font-size: 10px;
    text-align: center;
    color: rgba(0, 0, 0, 0.6);
}
.roteirizacao-motoristas-panel__legend {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 4px;
    vertical-align: middle;
}
.roteirizacao-motoristas-panel__timeline {
    position: relative;
    height: 14px;
    background: #eceff1;
    border-radius: 3px;
}
.roteirizacao-motoristas-panel__segment {
    position: absolute;
    top: 0;
    bottom: 0;
    border-radius: 2px;
}
</style>
